// ─────────────────────────────────────────────────────────────
// pnlReport.config.js — Report 8: profit and loss
// Shape: statement (portrait)
//
// Every figure is read from what PnLTab already fetched. Nothing
// is recomputed here — a printed statement that disagrees with
// the screen is worse than no statement at all.
//
// WHAT THE NUMBERS ARE
//   Revenue  invoiced in the period, not collected
//   COGS     frozen cost of exactly what was invoiced
//   Position balances as at today, NOT for the period — printed
//            in its own section so the two are never confused
// ─────────────────────────────────────────────────────────────

import { SIGNATURES, FORMATTERS } from '../../print/printConfig';

const num = (v) => (isNaN(parseFloat(v)) ? 0 : parseFloat(v));

// Expenses and COGS are shown as the negatives they are, so the
// column adds down the page instead of needing mental arithmetic.
const neg = (v) => -Math.abs(num(v));

export const pnlReportConfig = ({
  pnl, prevPnl, trend, balances, mode, periodLabel, period,
}) => {
  const r = pnl.revenue || {};
  const c = pnl.cogs || {};
  const e = pnl.expenses || { by_category: [], total: 0 };

  const revenue = num(r.total);
  const cogs = num(c.total);
  const gross = num(pnl.gross_profit);
  const expenses = num(e.total);
  const net = num(pnl.net_profit);

  // ── Statement rows ──
  // Sub-lines are marked with a bullet rather than leading
  // spaces — jsPDF collapses leading whitespace, so indentation
  // by padding simply disappears on the page.
  const statementRows = [
    {
      line: 'Revenue',
      detail: '',
      amount: revenue,
      __subtotal: true,
    },
    {
      line: '   · Tape sales',
      detail: `${r.invoice_count || 0} invoice${Number(r.invoice_count) === 1 ? '' : 's'}`,
      amount: num(r.sales),
      __tone: 'income',
    },
    {
      line: '   · Scrap sales',
      detail: num(r.scrap_kg) > 0 ? FORMATTERS.kg(r.scrap_kg) : '',
      amount: num(r.scrap),
      __tone: 'income',
    },
    {
      line: 'Cost of Goods Sold',
      detail: `${c.rolls_sold || 0} roll${Number(c.rolls_sold) === 1 ? '' : 's'} sold`,
      amount: neg(cogs),
      __tone: 'expense',
    },
    {
      line: 'GROSS PROFIT',
      detail: pnl.gross_margin_pct != null ? `${pnl.gross_margin_pct}% margin` : '',
      amount: gross,
      __subtotal: true,
    },
    {
      line: 'Operating Expenses',
      detail: '',
      amount: neg(expenses),
      __subtotal: true,
    },
  ];

  (e.by_category || []).forEach((cat) => {
    statementRows.push({
      line: `   · ${cat.category_name}`,
      detail: `${cat.entry_count} ${Number(cat.entry_count) === 1 ? 'entry' : 'entries'}`,
      amount: neg(cat.total),
      __tone: 'expense',
    });
  });

  if ((e.by_category || []).length === 0) {
    statementRows.push({
      line: '   · No expenses recorded',
      detail: '',
      amount: 0,
    });
  }

  statementRows.push({
    line: 'NET PROFIT',
    detail: pnl.net_margin_pct != null ? `${pnl.net_margin_pct}% margin` : '',
    amount: net,
    __subtotal: true,
  });

  const sections = [
    {
      title: 'Profit and Loss Statement',
      tone: net >= 0 ? 'income' : 'expense',
      columns: [
        { key: 'line', label: 'Particulars', align: 'left', fmt: 'text' },
        { key: 'detail', label: 'Detail', align: 'left', fmt: 'text', width: 40 },
        { key: 'amount', label: 'Amount', align: 'right', fmt: 'money', width: 36, bold: true },
      ],
      rows: statementRows,
    },
  ];

  // ── Position ──
  // Deliberately its own section with its own heading: these are
  // balances as at now and have no business inside a period P&L.
  if (balances) {
    sections.push({
      title: 'Position as at Today',
      tone: 'net',
      columns: [
        { key: 'line', label: 'Particulars', align: 'left', fmt: 'text' },
        { key: 'note', label: 'Note', align: 'left', fmt: 'text', width: 60 },
        { key: 'amount', label: 'Amount', align: 'right', fmt: 'money', width: 36, bold: true },
      ],
      rows: [
        {
          line: 'Customers owe us',
          note: 'Invoiced less received',
          amount: num(balances.receivable),
          __tone: 'income',
        },
        {
          line: 'We owe vendors',
          note: 'Purchased less paid',
          amount: num(balances.payable),
          __tone: 'expense',
        },
        {
          line: 'Finished stock at cost',
          note: 'Unsold rolls',
          amount: num(balances.finished_stock_value),
        },
        {
          line: 'Raw material at cost',
          note: 'Jumbo rolls remaining',
          amount: num(balances.raw_stock_value),
        },
        {
          line: 'Stock Held',
          note: '',
          amount: num(balances.finished_stock_value) + num(balances.raw_stock_value),
          __subtotal: true,
        },
      ],
    });
  }

  // ── Month by month ──
  if (mode === 'range' && (trend || []).length > 1) {
    const trendRows = trend.map((m) => ({
      month: m.month_label,
      revenue: num(m.sales_revenue) + num(m.scrap_revenue),
      cogs: num(m.cogs),
      expenses: num(m.expenses),
      net_profit: num(m.net_profit),
    }));

    const sum = (key) => trendRows.reduce((s, row) => s + num(row[key]), 0);

    sections.push({
      title: 'Month by Month',
      tone: 'net',
      columns: [
        { key: 'month', label: 'Month', align: 'left', fmt: 'text' },
        { key: 'revenue', label: 'Revenue', align: 'right', fmt: 'money', width: 30 },
        { key: 'cogs', label: 'COGS', align: 'right', fmt: 'money', width: 28 },
        { key: 'expenses', label: 'Expenses', align: 'right', fmt: 'money', width: 28 },
        { key: 'net_profit', label: 'Net', align: 'right', fmt: 'money', width: 30, bold: true },
      ],
      rows: trendRows,
      footRow: [
        'TOTAL',
        FORMATTERS.money(sum('revenue')),
        FORMATTERS.money(sum('cogs')),
        FORMATTERS.money(sum('expenses')),
        FORMATTERS.money(sum('net_profit')),
      ],
    });
  }

  // ── Summary box ──
  const summaryRows = [
    {
      label: 'Revenue',
      value: FORMATTERS.money(revenue),
      label2: 'COGS',
      value2: FORMATTERS.money(cogs),
    },
    {
      label: 'Gross Profit',
      value: FORMATTERS.money(gross),
      label2: 'Margin',
      value2: pnl.gross_margin_pct != null ? `${pnl.gross_margin_pct}%` : '—',
    },
    {
      label: 'Expenses',
      value: FORMATTERS.money(expenses),
      label2: 'Categories',
      value2: (e.by_category || []).length,
    },
    {
      label: 'Net Profit',
      value: FORMATTERS.money(net),
      label2: 'Margin',
      value2: pnl.net_margin_pct != null ? `${pnl.net_margin_pct}%` : '—',
    },
  ];

  // Last month's figures only mean something in monthly mode —
  // a custom range has no natural period before it.
  if (mode === 'monthly' && prevPnl) {
    summaryRows.push({
      label: 'Last Month Net',
      value: FORMATTERS.money(prevPnl.net_profit),
      label2: 'Change',
      value2: FORMATTERS.money(net - num(prevPnl.net_profit)),
    });
  }

  return {
    kind: 'statement',
    orientation: 'p',
    title: 'Profit & Loss',
    subtitle: periodLabel,

    boxes: [
      {
        heading: 'Period',
        rows: [
          { label: 'Basis', value: mode === 'monthly' ? 'Calendar Month' : 'Custom Range' },
          { label: 'From', value: FORMATTERS.date(period.from_date) },
          { label: 'To', value: FORMATTERS.date(period.to_date) },
          { label: 'Prepared', value: FORMATTERS.date(new Date()) },
        ],
      },
      {
        heading: 'Summary',
        rows: summaryRows,
      },
    ],

    sections,

    totals: [
      { label: 'Revenue', value: revenue, fmt: 'money', tone: 'income' },
      { label: 'Cost of Goods Sold', value: cogs, fmt: 'money', tone: 'expense' },
      { label: 'Gross Profit', value: gross, fmt: 'money' },
      { label: 'Operating Expenses', value: expenses, fmt: 'money', tone: 'expense' },
      {
        label: 'NET PROFIT',
        value: net,
        fmt: 'money',
        emphasis: true,
        tone: net >= 0 ? 'income' : 'expense',
      },
    ],

    termsHeading: 'NOTES:',
    terms: [
      'Revenue is what was invoiced in the period, not what was collected.',
      'Cost of goods sold is the traced cost of exactly what was sold, frozen at the moment of each invoice.',
      'Unsold stock is excluded from cost of goods and carried as inventory in the position section.',
      'Scrap income carries no cost — waste absorbs none in the costing chain.',
      'Position figures are balances as at today and do not relate to the period above.',
    ],

    signatures: SIGNATURES.report,
  };
};

export default pnlReportConfig;